import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const STATUS_ORDER = { open: 0, in_progress: 1, resolved: 2 };

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function supportError(error, fallback) {
  const raw = String(error?.message || '');
  if (/site_support_requests.*does not exist|relation .*site_support_requests/i.test(raw)) {
    return new Error('사이트 문의 데이터베이스 설정이 아직 적용되지 않았습니다.');
  }
  if (/permission denied|row-level security/i.test(raw)) {
    return new Error('사이트 문의를 사용할 권한이 없습니다. 로그인 상태를 확인해주세요.');
  }
  return new Error(raw || fallback || '사이트 문의 정보를 처리하지 못했습니다.');
}

async function currentContext() {
  const client = requireClient();
  const {
    data: { user },
    error,
  } = await client.auth.getUser();
  if (error || !user?.id) throw new Error('로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.');

  const metadata = user.user_metadata || {};
  return {
    viewerUserId: String(user.id),
    viewerName: String(metadata.nickname || metadata.name || user.email || '사용자'),
    viewerEmail: String(user.email || ''),
  };
}

async function isPlatformAdmin(client, userId) {
  const { data, error } = await client
    .from('platform_admins')
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw supportError(error, '플랫폼 관리자 권한을 확인하지 못했습니다.');
  return Boolean(data);
}

function normalizeRequest(row = {}) {
  return {
    id: String(row.id || ''),
    authorUserId: String(row.author_user_id || ''),
    authorName: String(row.author_name || '사용자'),
    authorEmail: String(row.author_email || ''),
    category: String(row.category || 'general'),
    title: String(row.title || ''),
    body: String(row.body || ''),
    status: String(row.status || 'open'),
    adminReply: String(row.admin_reply || ''),
    handledBy: String(row.handled_by || ''),
    handledAt: row.handled_at || null,
    createdAt: row.created_at || null,
    updatedAt: row.updated_at || null,
  };
}

function sortRequests(items) {
  return [...items].sort((a, b) => {
    const statusDiff = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
    if (statusDiff) return statusDiff;
    return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
  });
}

const REQUEST_COLUMNS = 'id,author_user_id,author_name,author_email,category,title,body,status,admin_reply,handled_by,handled_at,created_at,updated_at';

export async function loadSiteSupportRequests({ manage = false } = {}) {
  const client = requireClient();
  const context = await currentContext();
  const platformAdmin = await isPlatformAdmin(client, context.viewerUserId);
  if (manage && !platformAdmin) throw new Error('플랫폼 관리자만 사이트 문의를 관리할 수 있습니다.');

  let query = client
    .from('site_support_requests')
    .select(REQUEST_COLUMNS)
    .order('created_at', { ascending: false })
    .limit(300);
  if (!manage) query = query.eq('author_user_id', context.viewerUserId);

  const { data, error } = await query;
  if (error) throw supportError(error, '사이트 문의를 불러오지 못했습니다.');
  return { ...context, isPlatformAdmin: platformAdmin, requests: sortRequests((data || []).map(normalizeRequest)) };
}

export async function createSiteSupportRequest({ category = 'general', title, body }) {
  const cleanTitle = String(title || '').trim();
  const cleanBody = String(body || '').trim();
  if (!cleanTitle) throw new Error('문의 제목을 입력해주세요.');
  if (!cleanBody) throw new Error('문의 내용을 입력해주세요.');
  if (cleanTitle.length > 120) throw new Error('문의 제목은 120자 이하로 입력해주세요.');
  if (cleanBody.length > 5000) throw new Error('문의 내용은 5000자 이하로 입력해주세요.');

  const client = requireClient();
  const context = await currentContext();
  const { data, error } = await client
    .from('site_support_requests')
    .insert({
      author_user_id: context.viewerUserId,
      author_name: context.viewerName,
      author_email: context.viewerEmail,
      category: String(category || 'general'),
      title: cleanTitle,
      body: cleanBody,
    })
    .select(REQUEST_COLUMNS)
    .single();
  if (error) throw supportError(error, '사이트 문의를 등록하지 못했습니다.');
  window.dispatchEvent(new CustomEvent('doroparty:site-support-changed'));
  return normalizeRequest(data);
}

export async function updateSiteSupportRequest({ id, status, adminReply = '' }) {
  const requestId = String(id || '').trim();
  const cleanStatus = String(status || 'open');
  const cleanReply = String(adminReply || '').trim();
  if (!requestId) throw new Error('처리할 문의를 찾을 수 없습니다.');
  if (!['open', 'in_progress', 'resolved'].includes(cleanStatus)) throw new Error('문의 처리 상태를 확인해주세요.');
  if (cleanReply.length > 5000) throw new Error('답변은 5000자 이하로 입력해주세요.');

  const client = requireClient();
  const context = await currentContext();
  if (!await isPlatformAdmin(client, context.viewerUserId)) throw new Error('플랫폼 관리자만 사이트 문의를 처리할 수 있습니다.');
  const { data, error } = await client
    .from('site_support_requests')
    .update({ status: cleanStatus, admin_reply: cleanReply })
    .eq('id', requestId)
    .select(REQUEST_COLUMNS)
    .single();
  if (error) throw supportError(error, '사이트 문의 처리 내용을 저장하지 못했습니다.');
  window.dispatchEvent(new CustomEvent('doroparty:site-support-changed'));
  return normalizeRequest(data);
}
